<template>
  <div class="page">
    <div class="sidebar">
      <h2>Email Signature Generator</h2>

      <div class="field-container">
        <label>Full name <input type="text" v-model="fullName" /></label>
        <label>Title <input type="text" v-model="title" /></label>
      </div>

      <p class="section-title">Contact</p>
      <div class="field-container">
        <div
          v-for="(field, index) in contactFields"
          :key="field.key"
          class="draggable-field"
          draggable="true"
          @dragstart="dragStart(index)"
          @dragover.prevent="dragOver(index)"
          @drop="dragDrop"
          :class="{ 'drag-over': dragOverIndex === index }"
        >
          <span class="drag-handle">⠿</span>
          <label>
            {{ field.label }}
            <input
              type="text"
              :value="values[field.key]"
              @input="values[field.key] = $event.target.value"
            />
          </label>
        </div>
        <label>Maps Link <input type="text" v-model="values.map" /></label>
      </div>

      <button @click="copy">{{ copied ? 'Copied!' : 'Copy Signature' }}</button>
    </div>

    <div class="main">
      <div class="preview-card">
        <p class="preview-label">Preview</p>
        <table
          style="color: #151433; font-family: 'Open Sans', Arial, sans-serif; font-size: 14px; line-height: 1.4;"
          cellspacing="0"
          cellpadding="0"
          border="0"
          id="signature"
        >
          <tbody>
            <tr>
              <td style="vertical-align: top;">
                <table cellspacing="0" cellpadding="0" border="0"><tbody><tr>
                  <td style="vertical-align: top; line-height: 0;">
                    <a :href="'https://' + values.website + '/'" style="text-decoration: none;"><img
                      src="https://signature.dev.ionata.com/ionata_logo.png"
                      style="width: 100px; height: 100px; border-radius: 50%; object-fit: cover;"
                    /></a>
                  </td>
                  <td style="padding: 0 20px; vertical-align: top;">
                    <div style="width: 1px; height: 80px; background-color: #151433; margin-top: 10px;"></div>
                  </td>
                </tr></tbody></table>
              </td>
              <td style="vertical-align: middle;">
                <div style="margin-bottom: 6px; line-height: 1.2;">
                  <div style="font-size: 16px; font-weight: 600;">{{ fullName }}</div>
                  <div style="font-size: 14px;">{{ title }}</div>
                </div>
                <div style="color: #151433; font-family: Consolas, Menlo, 'Lucida Console', monospace; font-size: 13px;">
                  <template v-for="field in contactFields">
                    <template v-if="values[field.key]">
                      <strong :key="field.key + '_label'">{{ field.prefix }}: </strong><a :key="field.key + '_val'" :href="getHref(field)" style="color: #151433; text-decoration: none;">{{ values[field.key] }}</a><br :key="field.key + '_br'">
                    </template>
                  </template>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="instructions">
        <h3>Adding to Gmail</h3>
        <ol>
          <li>Click <strong>Copy Signature</strong> above.</li>
          <li>Open <strong>Gmail</strong> and go to <strong>Settings</strong> (gear icon) &rsaquo; <strong>See all settings</strong>.</li>
          <li>Under the <strong>General</strong> tab, scroll to <strong>Signature</strong> and click <strong>Create new</strong>.</li>
          <li>Give it a name, then paste the signature into the text box (<strong>Cmd+V</strong>).</li>
          <li>Scroll down and click <strong>Save Changes</strong>.</li>
        </ol>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "App",
  data() {
    return {
      fullName: "Martin Anderson",
      title: "CEO + Founder",
      copied: false,
      dragIndex: null,
      dragOverIndex: null,
      contactFields: [
        { key: 'email',   label: 'Email',   prefix: 'E' },
        { key: 'mobile',  label: 'Mobile',  prefix: 'M' },
        { key: 'phone',   label: 'Phone',   prefix: 'T' },
        { key: 'website', label: 'Website', prefix: 'W' },
        { key: 'address', label: 'Address', prefix: 'A' },
      ],
      values: {
        email:   '',
        mobile:  '0438 515 595',
        phone:   '03 6231 9110',
        website: 'ionata.com.au',
        address: '73a Rupert St, Collingwood',
        map:     'https://www.google.com/maps/place/Ionata+Digital/data=!4m2!3m1!1s0x0:0x298c498d083d6af3?sa=X&ved=1t:2428&ictx=111',
      },
    };
  },
  methods: {
    getHref(field) {
      const v = this.values[field.key];
      if (field.key === 'email')   return 'mailto:' + v;
      if (field.key === 'mobile')  return 'tel:' + v;
      if (field.key === 'phone')   return 'tel:' + v;
      if (field.key === 'website') return 'https://' + v + '/';
      if (field.key === 'address') return this.values.map;
      return '#';
    },
    dragStart(index) {
      this.dragIndex = index;
    },
    dragOver(index) {
      this.dragOverIndex = index;
    },
    dragDrop() {
      const fields = [...this.contactFields];
      const [moved] = fields.splice(this.dragIndex, 1);
      fields.splice(this.dragOverIndex, 0, moved);
      this.contactFields = fields;
      this.dragIndex = null;
      this.dragOverIndex = null;
    },
    copy() {
      const signature = document.querySelector("#signature");
      const html = signature.outerHTML;
      // eslint-disable-next-line no-undef
      const item = new ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([signature.innerText], { type: 'text/plain' }),
      });
      navigator.clipboard.write([item]).then(() => {
        this.copied = true;
        setTimeout(() => { this.copied = false; }, 2000);
      });
    },
  },
};
</script>

<style>
*, *::before, *::after {
  box-sizing: border-box;
}

body {
  margin: 0;
  background: #f4f5f7;
  font-family: Arial, sans-serif;
  color: #151433;
}

.page {
  display: flex;
  min-height: 100vh;
}

.sidebar {
  width: 280px;
  flex-shrink: 0;
  background: #fff;
  border-right: 1px solid #e0e0e8;
  padding: 32px 24px;
  display: flex;
  flex-direction: column;
}

.sidebar h2 {
  font-size: 16px;
  font-weight: 700;
  margin: 0 0 24px;
  color: #151433;
}

.section-title {
  font-size: 12px;
  font-weight: 600;
  color: #555;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin: 20px 0 12px;
  padding-top: 20px;
  border-top: 1px solid #e0e0e8;
}

.field-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

label {
  display: flex;
  flex-direction: column;
  font-size: 12px;
  font-weight: 600;
  color: #555;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  gap: 4px;
}

input {
  font-size: 14px;
  padding: 6px 10px;
  border: 1px solid #d0d0da;
  border-radius: 6px;
  color: #151433;
  outline: none;
}

input:focus {
  border-color: #151433;
}

.draggable-field {
  display: flex;
  align-items: flex-end;
  gap: 6px;
  border-radius: 6px;
  transition: background 0.1s;
}

.draggable-field label {
  flex: 1;
}

.draggable-field.drag-over {
  background: #f0f0f8;
  outline: 2px dashed #c0c0d8;
}

.drag-handle {
  font-size: 16px;
  color: #ccc;
  cursor: grab;
  padding-bottom: 7px;
  user-select: none;
  flex-shrink: 0;
}

.drag-handle:active {
  cursor: grabbing;
}

button {
  margin-top: 24px;
  padding: 10px;
  background: #151433;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s;
}

button:hover {
  opacity: 0.85;
}

.main {
  flex: 1;
  padding: 40px;
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.preview-card {
  background: #fff;
  border: 1px solid #e0e0e8;
  border-radius: 10px;
  padding: 32px;
  display: inline-block;
}

.preview-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #999;
  margin: 0 0 20px;
}

.instructions {
  background: #fff;
  border: 1px solid #e0e0e8;
  border-radius: 10px;
  padding: 28px 32px;
  max-width: 520px;
}

.instructions h3 {
  font-size: 15px;
  font-weight: 700;
  margin: 0 0 16px;
}

.instructions ol {
  margin: 0;
  padding-left: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.instructions li {
  font-size: 14px;
  line-height: 1.5;
  color: #333;
}
</style>
